import { useCallback, useEffect, useState } from 'react'

import { DENSITY_STORAGE_KEY, DensityContext, type Density } from '@/hooks/useDensity'

/**
 * Row density, applied to `<html>` as `data-density`.
 *
 * A preference, not view state: it follows the *person* across every page, so
 * it persists to storage rather than to the URL. A URL carrying it would mean
 * sharing a filtered table also imposed your row height on the recipient
 * (CLAUDE.md §4.5).
 *
 * Its own provider rather than a field on the theme's: the two are separate
 * preferences, written to separate attributes and storage keys.
 *
 * `compact` is the default and writes no attribute at all — the dense-neutral
 * design language is built around it, and the CSS treats absent as compact, so
 * there is only one way to express the default.
 */
function apply(density: Density) {
  if (density === 'comfortable') {
    document.documentElement.setAttribute('data-density', 'comfortable')
  } else {
    document.documentElement.removeAttribute('data-density')
  }
}

function stored(): Density {
  try {
    return window.localStorage.getItem(DENSITY_STORAGE_KEY) === 'comfortable'
      ? 'comfortable'
      : 'compact'
  } catch {
    // Private mode, or storage blocked. Falling through to the default is
    // correct and readable.
    return 'compact'
  }
}

export function DensityProvider({ children }: { children: React.ReactNode }) {
  const [density, setState] = useState<Density>(stored)

  // The inline script in index.html has already applied this before first
  // paint; this keeps the DOM in step when the value changes afterwards.
  useEffect(() => apply(density), [density])

  const setDensity = useCallback((next: Density) => {
    setState(next)
    try {
      window.localStorage.setItem(DENSITY_STORAGE_KEY, next)
    } catch {
      // Unpersisted beats unusable: the choice still applies for this session.
    }
  }, [])

  return <DensityContext value={{ density, setDensity }}>{children}</DensityContext>
}
